import React, { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Spin, Alert, Collapse } from 'antd';
import AuthImage from 'components/Elements/AuthImage';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/types';
import { DoorCctvCode, DoorCctvCodeHistoryEntry } from 'core/sites/types';
import { fetchDoorCctvCodes, fetchDoorCctvCodeHistory } from 'core/sites/sitesApi';
import './doorCctvCodesModal.scss';

const CollapseExpandIcon: FC<{ isActive?: boolean }> = ({ isActive }) => (
    <svg
        width="20"
        height="13"
        viewBox="0 0 22 13"
        fill="none"
        style={{
            transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s',
        }}>
        <path d="M21 1.5L11 11.5L1 1.5" stroke="#2B2D42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

interface DoorCctvCodesModalProps {
    visible: boolean;
    onClose: () => void;
    encryptedId: string;
}

const DoorCctvCodesModal: FC<DoorCctvCodesModalProps> = ({
    visible,
    onClose,
    encryptedId,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const mountedRef = useRef(true);

    const [doorCctvCode, setDoorCctvCode] = useState<DoorCctvCode | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [history, setHistory] = useState<DoorCctvCodeHistoryEntry[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);

    useEffect(() => () => { mountedRef.current = false; }, []);

    const loadDoorCctvCodes = useCallback(async () => {
        if (!encryptedId) return;
        setIsLoading(true);
        setError(null);
        setDoorCctvCode(null);
        try {
            const result = await dispatch(fetchDoorCctvCodes(encryptedId)).unwrap();
            if (!mountedRef.current) return;
            if (result.doorCctvCodes && result.doorCctvCodes.length > 0) {
                setDoorCctvCode(result.doorCctvCodes[0]);
            } else {
                setDoorCctvCode(null);
            }
        } catch (err) {
            if (!mountedRef.current) return;
            setError(typeof err === 'string' ? err : 'Failed to load door & CCTV codes');
        } finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [dispatch, encryptedId]);

    useEffect(() => {
        if (visible) {
            loadDoorCctvCodes();
            setHistory([]);
            setHistoryLoaded(false);
            setIsLoadingHistory(false);
            setHistoryError(null);
        } else {
            setDoorCctvCode(null);
            setIsLoading(false);
            setError(null);
            setHistory([]);
            setHistoryLoaded(false);
            setIsLoadingHistory(false);
            setHistoryError(null);
        }
    }, [visible, encryptedId, loadDoorCctvCodes]);

    const loadHistory = async () => {
        if (historyLoaded || !encryptedId) return;
        setIsLoadingHistory(true);
        setHistoryError(null);
        try {
            const result = await dispatch(fetchDoorCctvCodeHistory({ encryptedId })).unwrap();
            if (!mountedRef.current) return;
            setHistory(result.data);
            setHistoryLoaded(true);
        } catch {
            if (!mountedRef.current) return;
            setHistoryError('Failed to load code history');
        } finally {
            if (mountedRef.current) {
                setIsLoadingHistory(false);
            }
        }
    };

    const handleCollapseChange = (keys: string | string[]) => {
        const activeKeys = Array.isArray(keys) ? keys : [keys];
        if (activeKeys.length > 0 && (!historyLoaded || historyError)) {
            loadHistory();
        }
    };

    // API returns the 3 most recent previous codes, newest first; filter out consecutive duplicates
    const filteredHistory = useMemo(() => {
        if (!doorCctvCode) return history;
        return history.filter((entry, index) => {
            const doorImage = entry.backOfficeDoorCodeImage;
            const cctvImage = entry.cctvPasswordImage;
            if (!doorImage && !cctvImage) return false;
            const newerDoor = index === 0
                ? doorCctvCode.backOfficeDoorCodeImage
                : history[index - 1]?.backOfficeDoorCodeImage ?? null;
            const newerCctv = index === 0
                ? doorCctvCode.cctvPasswordImage
                : history[index - 1]?.cctvPasswordImage ?? null;
            return doorImage !== newerDoor || cctvImage !== newerCctv;
        });
    }, [history, doorCctvCode]);

    const renderHistoryList = (): React.ReactNode => {
        if (isLoadingHistory) {
            return (
                <div className="history-loading">
                    <Spin size="small" />
                </div>
            );
        }

        if (historyError) {
            return <Alert type="error" message={historyError} showIcon />;
        }

        if (filteredHistory.length === 0) {
            return <div className="history-empty">No previous codes found.</div>;
        }
        return (
            <div className="history-list">
                <div className="history-header">
                    <span className="history-header-label">Door Code</span>
                    <span className="history-header-label">CCTV Code</span>
                </div>
                {filteredHistory.map((entry) => (
                    <div key={entry.id} className="history-item">
                        <span className="history-code">
                            {entry.backOfficeDoorCodeImage
                                ? <AuthImage src={entry.backOfficeDoorCodeImage} alt="Door code" />
                                : <span className="no-code-placeholder">-</span>}
                        </span>
                        <span className="history-code">
                            {entry.cctvPasswordImage
                                ? <AuthImage src={entry.cctvPasswordImage} alt="CCTV code" />
                                : <span className="no-code-placeholder">-</span>}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <Modal
            title="Door & CCTV Codes"
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width={800}
            wrapClassName="door-cctv-codes-modal">
            {isLoading && (
                <div className="modal-loading">
                    <Spin size="large" />
                </div>
            )}
            {!isLoading && error && (
                <Alert type="error" message={error} showIcon />
            )}
            {!isLoading && !error && !doorCctvCode && (
                <div className="codes-empty">No codes available for this site.</div>
            )}
            {!isLoading && !error && doorCctvCode && (
                <>
                    <div className="codes-grid">
                        <div className="code-column">
                            <div className="code-label">Door Code:</div>
                            <div className="code-image-container">
                                {doorCctvCode.backOfficeDoorCodeImage
                                    ? <AuthImage src={doorCctvCode.backOfficeDoorCodeImage} alt="Door Code" className="code-image" preview />
                                    : <span className="no-code-placeholder">No code set</span>}
                            </div>
                        </div>
                        <div className="code-column">
                            <div className="code-label">CCTV Code:</div>
                            <div className="code-image-container">
                                {doorCctvCode.cctvPasswordImage
                                    ? <AuthImage src={doorCctvCode.cctvPasswordImage} alt="CCTV Code" className="code-image" preview />
                                    : <span className="no-code-placeholder">No code set</span>}
                            </div>
                        </div>
                    </div>
                    <div className="code-history">
                        <Collapse
                            expandIconPosition="end"
                            expandIcon={CollapseExpandIcon}
                            onChange={handleCollapseChange}
                            items={[{
                                key: 'history',
                                label: 'View Code History',
                                children: renderHistoryList(),
                            }]} />
                    </div>
                </>
            )}
        </Modal>
    );
};

export default DoorCctvCodesModal;
